'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Reaction {
  emoji: string;
  label: string;
  count: number;
}

interface DirectMessage {
  id: string;
  from: string;
  fromRole: string;
  preview: string;
  timestamp: string;
  unread: boolean;
}

interface Comment {
  id: string;
  author: string;
  authorRole: string;
  authorCity?: string;
  anonymous: boolean;
  content: string;
  timestamp: string;
  reactions: Reaction[];
  replies: Comment[];
  chainRef?: string;
}

interface ObservationThread {
  id: string;
  title: string;
  arabic?: string;
  chainRef: string;
  nameRef: string;
  color: string;
  commentCount: number;
  contributorCount: number;
  lastActivity: string;
  comments: Comment[];
}

// ─── MOCK DATA ────────────────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  'inner-seeker': '#8a8ab8',
  'community-caretaker': '#2d7a47',
  'academic': '#d4a82a',
  'activist': '#2d8a87',
  'explorer': '#c9a84c',
};

const EMOJI_REACTIONS = [
  { emoji: '🌿', label: 'Barakah' },
  { emoji: '✦', label: 'Subhanallah' },
  { emoji: '🤲', label: 'Alhamdulillah' },
  { emoji: '💡', label: 'Insightful' },
  { emoji: '🔗', label: 'Connected' },
];

const OBSERVATION_THREADS: ObservationThread[] = [
  {
    id: 'ot1',
    title: 'Mycorrhizal Networks as Al-Wadud',
    arabic: 'الْوَدُود',
    chainRef: 'Leaf Sequence · Chain 04',
    nameRef: 'Al-Wadud',
    color: '#2d7a47',
    commentCount: 14,
    contributorCount: 7,
    lastActivity: '2 hours ago',
    comments: [
      {
        id: 'c1',
        author: 'Sister Amira',
        authorRole: 'community-caretaker',
        authorCity: 'Atlanta',
        anonymous: false,
        content: 'The fungal network beneath the forest floor mirrors the invisible bonds of community care — each node giving without expectation of return. This is Al-Wadud made visible in creation.',
        timestamp: '2 hours ago',
        reactions: [
          { emoji: '🌿', label: 'Barakah', count: 8 },
          { emoji: '✦', label: 'Subhanallah', count: 12 },
          { emoji: '🔗', label: 'Connected', count: 5 },
        ],
        chainRef: 'Leaf Sequence · Chain 04',
        replies: [
          {
            id: 'c1r1',
            author: 'Dr. Yusuf',
            authorRole: 'academic',
            authorCity: 'Durham',
            anonymous: false,
            content: 'This resonates with Ibn Arabi\'s concept of the Breath of the Merciful — Al-Rahman exhaling creation into existence through interconnected webs of provision.',
            timestamp: '1 hour ago',
            reactions: [{ emoji: '💡', label: 'Insightful', count: 6 }],
            replies: [],
          },
        ],
      },
      {
        id: 'c2',
        author: 'A Caretaker',
        authorRole: 'inner-seeker',
        authorCity: 'Chicago',
        anonymous: true,
        content: 'I observed this in my neighborhood — the way elders pass knowledge to youth without formal structure. The network is already there. The IRL Lens just gave me language for it.',
        timestamp: '45 min ago',
        reactions: [
          { emoji: '🤲', label: 'Alhamdulillah', count: 9 },
          { emoji: '🌿', label: 'Barakah', count: 4 },
        ],
        replies: [],
      },
    ],
  },
  {
    id: 'ot2',
    title: 'Water Cycle as Ar-Razzaq in Motion',
    arabic: 'الرَّزَّاق',
    chainRef: 'Water Sequence · Chain 07',
    nameRef: 'Ar-Razzaq',
    color: '#2d8a87',
    commentCount: 9,
    contributorCount: 5,
    lastActivity: '5 hours ago',
    comments: [
      {
        id: 'c3',
        author: 'Brother Tariq',
        authorRole: 'inner-seeker',
        authorCity: 'Portland',
        anonymous: false,
        content: 'The water cycle never stops — evaporation, condensation, precipitation. Ar-Razzaq is not a one-time event. It is a continuous process. The provision is always in motion.',
        timestamp: '5 hours ago',
        reactions: [
          { emoji: '✦', label: 'Subhanallah', count: 15 },
          { emoji: '🤲', label: 'Alhamdulillah', count: 7 },
        ],
        replies: [],
      },
    ],
  },
  {
    id: 'ot3',
    title: 'Urban Decay as Al-Mumit / Al-Muhyi',
    arabic: 'الْمُمِيت · الْمُحْيِي',
    chainRef: 'Justice Sequence · Chain 02',
    nameRef: 'Al-Mumit / Al-Muhyi',
    color: '#8a8ab8',
    commentCount: 21,
    contributorCount: 11,
    lastActivity: '1 day ago',
    comments: [
      {
        id: 'c4',
        author: 'Ustadha Khadija',
        authorRole: 'activist',
        authorCity: 'Houston',
        anonymous: false,
        content: 'Abandoned lots in our neighborhood are not just neglect — they are Al-Mumit at work, clearing space for what comes next. The question is: who will be Al-Muhyi? Who will bring life back?',
        timestamp: '1 day ago',
        reactions: [
          { emoji: '🔗', label: 'Connected', count: 11 },
          { emoji: '💡', label: 'Insightful', count: 8 },
          { emoji: '🌿', label: 'Barakah', count: 6 },
        ],
        replies: [],
      },
    ],
  },
];

const MOCK_MESSAGES: DirectMessage[] = [
  {
    id: 'dm1',
    from: 'Dr. Yusuf',
    fromRole: 'academic',
    preview: 'Your observation on the mycorrhizal chain — have you read Ibn Arabi\'s Fusus al-Hikam on Al-Wadud?',
    timestamp: '1 hour ago',
    unread: true,
  },
  {
    id: 'dm2',
    from: 'Ustadha Khadija',
    fromRole: 'activist',
    preview: 'We\'re organizing a community mapping session in Houston next month. Would love your IRL Lens perspective.',
    timestamp: '3 hours ago',
    unread: true,
  },
  {
    id: 'dm3',
    from: 'Sister Amira',
    fromRole: 'community-caretaker',
    preview: 'Alhamdulillah for your reply. The Atlanta MRG team is using your chain observation in our next session.',
    timestamp: '1 day ago',
    unread: false,
  },
];

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function ReactionBar({ reactions, onReact }: { reactions: Reaction[]; onReact: (label: string) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
      {reactions.map((r) => (
        <button
          key={r.label}
          onClick={() => onReact(r.label)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            background: 'rgba(201,168,76,0.06)',
            border: '1px solid rgba(201,168,76,0.18)',
            borderRadius: 20,
            cursor: 'pointer',
            transition: 'background 0.2s',
            color: '#d4c5a9',
            fontSize: 11,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.14)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.06)'; }}
          title={r.label}
        >
          <span style={{ fontSize: 13 }}>{r.emoji}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.7)' }}>{r.count}</span>
        </button>
      ))}
      {/* Add reaction picker */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 10px',
            background: 'transparent',
            border: '1px dashed rgba(255,255,255,0.1)',
            borderRadius: 20,
            cursor: 'pointer',
            color: 'rgba(212,197,169,0.3)',
            fontSize: 11,
            fontFamily: 'monospace',
          }}
          onClick={() => onReact('add')}
          title="Add reaction"
        >
          + React
        </button>
      </div>
    </div>
  );
}

function CommentCard({
  comment,
  depth = 0,
  threadColor,
}: {
  comment: Comment;
  depth?: number;
  threadColor: string;
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [reactions, setReactions] = useState(comment.reactions);

  const handleReact = (label: string) => {
    if (label === 'add') return;
    setReactions(prev =>
      prev.map(r => r.label === label ? { ...r, count: r.count + 1 } : r)
    );
  };

  const roleColor = ROLE_COLORS[comment.authorRole] || '#c9a84c';

  return (
    <div
      style={{
        marginLeft: depth > 0 ? 24 : 0,
        borderLeft: depth > 0 ? `2px solid ${threadColor}25` : 'none',
        paddingLeft: depth > 0 ? 16 : 0,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          background: depth === 0 ? 'rgba(6,13,18,0.6)' : 'rgba(6,13,18,0.4)',
          border: `1px solid ${threadColor}15`,
          borderRadius: 8,
          padding: '14px 16px',
        }}
      >
        {/* Author row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: `${roleColor}20`,
              border: `1px solid ${roleColor}40`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 9, color: roleColor }}>
              {comment.anonymous ? '◎' : comment.author.charAt(0)}
            </span>
          </div>
          <div>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 10,
                color: roleColor,
                fontWeight: 600,
              }}
            >
              {comment.anonymous ? 'A Caretaker' : comment.author}
            </span>
            {comment.authorCity && !comment.anonymous && (
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', marginLeft: 6 }}>
                · {comment.authorCity}
              </span>
            )}
          </div>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 8,
              color: 'rgba(212,197,169,0.25)',
              marginLeft: 'auto',
            }}
          >
            {comment.timestamp}
          </span>
        </div>

        {/* Content */}
        <p
          style={{
            color: 'rgba(212,197,169,0.8)',
            fontSize: 13,
            lineHeight: 1.75,
            fontStyle: 'italic',
            marginBottom: 8,
          }}
        >
          {comment.content}
        </p>

        {/* Chain ref */}
        {comment.chainRef && (
          <div style={{ marginBottom: 8 }}>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: `${threadColor}60`,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                padding: '2px 8px',
                background: `${threadColor}08`,
                border: `1px solid ${threadColor}15`,
                borderRadius: 3,
              }}
            >
              ⛓ {comment.chainRef}
            </span>
          </div>
        )}

        {/* Reactions */}
        <ReactionBar reactions={reactions} onReact={handleReact} />

        {/* Reply button */}
        {depth < 2 && (
          <button
            onClick={() => setShowReply(!showReply)}
            style={{
              marginTop: 10,
              fontFamily: 'monospace',
              fontSize: 9,
              color: 'rgba(212,197,169,0.35)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              padding: 0,
            }}
          >
            {showReply ? '↑ Cancel' : '↩ Reply'}
          </button>
        )}

        {/* Reply input */}
        {showReply && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <textarea
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Add your observation..."
              rows={2}
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(201,168,76,0.15)',
                borderRadius: 4,
                padding: '8px 12px',
                color: '#d4c5a9',
                fontSize: 12,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
              }}
            />
            <button
              onClick={() => { setReplyText(''); setShowReply(false); }}
              style={{
                padding: '8px 14px',
                background: 'rgba(45,122,71,0.15)',
                border: '1px solid rgba(45,122,71,0.3)',
                borderRadius: 4,
                color: '#2d7a47',
                fontFamily: 'monospace',
                fontSize: 9,
                cursor: 'pointer',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                alignSelf: 'flex-end',
              }}
            >
              Post
            </button>
          </div>
        )}
      </div>

      {/* Nested replies */}
      {comment.replies.map(reply => (
        <CommentCard key={reply.id} comment={reply} depth={depth + 1} threadColor={threadColor} />
      ))}
    </div>
  );
}

function ObservationThreadPanel({ thread }: { thread: ObservationThread }) {
  const [expanded, setExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [anonymous, setAnonymous] = useState(false);

  return (
    <div
      style={{
        background: 'rgba(6,13,18,0.7)',
        border: `1px solid ${thread.color}20`,
        borderLeft: `3px solid ${thread.color}50`,
        borderRadius: 10,
        overflow: 'hidden',
        marginBottom: 12,
      }}
    >
      {/* Thread header */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {thread.arabic && (
          <span
            style={{
              fontFamily: "'Amiri', serif",
              fontSize: 22,
              color: thread.color,
              direction: 'rtl',
              flexShrink: 0,
            }}
          >
            {thread.arabic}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontFamily: 'monospace',
              fontSize: 11,
              color: '#F5F0E8',
              fontWeight: 600,
              letterSpacing: '.04em',
              marginBottom: 4,
              textAlign: 'left',
            }}
          >
            {thread.title}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: `${thread.color}70`,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
              }}
            >
              ⛓ {thread.chainRef}
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>
              {thread.commentCount} observations · {thread.contributorCount} contributors
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.2)' }}>
              {thread.lastActivity}
            </span>
          </div>
        </div>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: 10,
            color: `${thread.color}50`,
            flexShrink: 0,
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ↓
        </span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {/* Comments */}
          <div style={{ marginBottom: 16 }}>
            {thread.comments.map(comment => (
              <CommentCard key={comment.id} comment={comment} threadColor={thread.color} />
            ))}
          </div>

          {/* Add observation */}
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${thread.color}15`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            <p
              style={{
                fontFamily: 'monospace',
                fontSize: 9,
                color: `${thread.color}60`,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                marginBottom: 10,
              }}
            >
              ✦ Co-Contribute to This Chain
            </p>
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Share your observation, connection, or reflection on this chain..."
              rows={3}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(201,168,76,0.12)',
                borderRadius: 4,
                padding: '10px 14px',
                color: '#d4c5a9',
                fontSize: 13,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: 10,
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  fontFamily: 'monospace',
                  fontSize: 9,
                  color: 'rgba(212,197,169,0.4)',
                  letterSpacing: '.08em',
                }}
              >
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={e => setAnonymous(e.target.checked)}
                  style={{ accentColor: thread.color }}
                />
                Post anonymously
              </label>
              <button
                onClick={() => setNewComment('')}
                style={{
                  padding: '8px 20px',
                  background: `${thread.color}15`,
                  border: `1px solid ${thread.color}35`,
                  borderRadius: 4,
                  color: thread.color,
                  fontFamily: 'monospace',
                  fontSize: 9,
                  cursor: 'pointer',
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                }}
              >
                Post Observation →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DirectMessagesPanel() {
  const [activeMessage, setActiveMessage] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [composeTo, setComposeTo] = useState('');

  const unreadCount = MOCK_MESSAGES.filter(m => m.unread).length;

  return (
    <div
      style={{
        background: 'rgba(6,13,18,0.7)',
        border: '1px solid rgba(45,138,135,0.2)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(45,138,135,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#2d8a87', letterSpacing: '.15em', textTransform: 'uppercase' }}>
            Direct Messages
          </span>
          {unreadCount > 0 && (
            <span
              style={{
                background: '#2d8a87',
                color: '#060d12',
                fontFamily: 'monospace',
                fontSize: 8,
                padding: '2px 6px',
                borderRadius: 10,
                fontWeight: 700,
              }}
            >
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={() => setComposeOpen(!composeOpen)}
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            color: '#2d8a87',
            background: 'rgba(45,138,135,0.1)',
            border: '1px solid rgba(45,138,135,0.25)',
            borderRadius: 4,
            padding: '4px 12px',
            cursor: 'pointer',
            letterSpacing: '.1em',
            textTransform: 'uppercase',
          }}
        >
          + Compose
        </button>
      </div>

      {/* Compose panel */}
      {composeOpen && (
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid rgba(45,138,135,0.1)',
            background: 'rgba(45,138,135,0.04)',
          }}
        >
          <input
            type="text"
            placeholder="To: contributor name or role..."
            value={composeTo}
            onChange={e => setComposeTo(e.target.value)}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(45,138,135,0.2)',
              borderRadius: 4,
              padding: '8px 12px',
              color: '#d4c5a9',
              fontSize: 12,
              fontFamily: 'inherit',
              outline: 'none',
              marginBottom: 8,
              boxSizing: 'border-box',
            }}
          />
          <textarea
            placeholder="Your message..."
            value={composeText}
            onChange={e => setComposeText(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(45,138,135,0.2)',
              borderRadius: 4,
              padding: '8px 12px',
              color: '#d4c5a9',
              fontSize: 12,
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 8,
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setComposeOpen(false); setComposeText(''); setComposeTo(''); }}
              style={{
                fontFamily: 'monospace',
                fontSize: 9,
                color: 'rgba(212,197,169,0.4)',
                background: 'none',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 4,
                padding: '6px 14px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => { setComposeOpen(false); setComposeText(''); setComposeTo(''); }}
              style={{
                fontFamily: 'monospace',
                fontSize: 9,
                color: '#2d8a87',
                background: 'rgba(45,138,135,0.12)',
                border: '1px solid rgba(45,138,135,0.3)',
                borderRadius: 4,
                padding: '6px 14px',
                cursor: 'pointer',
                letterSpacing: '.1em',
                textTransform: 'uppercase',
              }}
            >
              Send →
            </button>
          </div>
        </div>
      )}

      {/* Message list */}
      <div>
        {MOCK_MESSAGES.map(msg => {
          const roleColor = ROLE_COLORS[msg.fromRole] || '#c9a84c';
          return (
            <button
              key={msg.id}
              onClick={() => setActiveMessage(activeMessage === msg.id ? null : msg.id)}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: activeMessage === msg.id ? 'rgba(45,138,135,0.06)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => { if (activeMessage !== msg.id) (e.currentTarget as HTMLElement).style.background = 'rgba(45,138,135,0.03)'; }}
              onMouseLeave={e => { if (activeMessage !== msg.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: `${roleColor}18`,
                  border: `1px solid ${roleColor}35`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  position: 'relative',
                }}
              >
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: roleColor }}>
                  {msg.from.charAt(0)}
                </span>
                {msg.unread && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#2d8a87',
                      border: '1.5px solid #060d12',
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 10,
                      color: msg.unread ? '#F5F0E8' : 'rgba(212,197,169,0.6)',
                      fontWeight: msg.unread ? 700 : 400,
                    }}
                  >
                    {msg.from}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)' }}>
                    {msg.timestamp}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 11,
                    color: msg.unread ? 'rgba(212,197,169,0.7)' : 'rgba(212,197,169,0.35)',
                    lineHeight: 1.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                >
                  {msg.preview}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Expanded message view */}
      {activeMessage && (() => {
        const msg = MOCK_MESSAGES.find(m => m.id === activeMessage)!;
        const roleColor = ROLE_COLORS[msg.fromRole] || '#c9a84c';
        return (
          <div
            style={{
              padding: '16px 20px',
              borderTop: '1px solid rgba(45,138,135,0.12)',
              background: 'rgba(45,138,135,0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 10, color: roleColor }}>{msg.from}</span>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>· {msg.fromRole.replace('-', ' ')}</span>
            </div>
            <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 13, lineHeight: 1.7, marginBottom: 14 }}>
              {msg.preview}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Reply..."
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(45,138,135,0.2)',
                  borderRadius: 4,
                  padding: '8px 12px',
                  color: '#d4c5a9',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
              <button
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  color: '#2d8a87',
                  background: 'rgba(45,138,135,0.12)',
                  border: '1px solid rgba(45,138,135,0.3)',
                  borderRadius: 4,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                }}
              >
                Send
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function CollaborationSpace() {
  const [activeTab, setActiveTab] = useState<'observations' | 'messages'>('observations');

  const tabs = [
    { id: 'observations' as const, label: 'Observation Threads', arabic: 'مشاركة المشاهدات', count: OBSERVATION_THREADS.length },
    { id: 'messages' as const, label: 'Direct Messages', arabic: 'رسائل مباشرة', count: MOCK_MESSAGES.filter(m => m.unread).length },
  ];

  return (
    <section
      style={{
        background: 'linear-gradient(180deg, #060d12 0%, #0a1018 100%)',
        padding: 'clamp(56px, 8vw, 96px) clamp(16px, 4vw, 32px)',
        borderTop: '1px solid rgba(45,138,135,0.1)',
      }}
    >
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <p
            style={{
              fontFamily: 'monospace',
              fontSize: 9,
              letterSpacing: '.22em',
              textTransform: 'uppercase',
              color: 'rgba(45,138,135,0.5)',
              marginBottom: 10,
            }}
          >
            فضاء التعاون · COLLABORATION SPACE
          </p>
          <h2
            style={{
              fontFamily: 'monospace',
              fontSize: 'clamp(14px, 2.5vw, 20px)',
              letterSpacing: '.15em',
              textTransform: 'uppercase',
              color: '#F5F0E8',
              marginBottom: 12,
            }}
          >
            Caretaker Collaboration Space
          </h2>
          <p
            style={{
              color: 'rgba(212,197,169,0.5)',
              fontSize: 14,
              fontStyle: 'italic',
              maxWidth: 560,
              margin: '0 auto',
              lineHeight: 1.8,
            }}
          >
            Comment on observations, co-contribute to chains, react with meaning, and message contributors directly. The work of caretaking is communal.
          </p>
        </div>

        {/* Tab navigation */}
        <div
          style={{
            display: 'flex',
            gap: 4,
            marginBottom: 28,
            background: 'rgba(6,13,18,0.6)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 8,
            padding: 4,
          }}
        >
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: activeTab === tab.id ? 'rgba(45,138,135,0.15)' : 'transparent',
                border: activeTab === tab.id ? '1px solid rgba(45,138,135,0.3)' : '1px solid transparent',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: activeTab === tab.id ? '#2d8a87' : 'rgba(212,197,169,0.35)',
                }}
              >
                {tab.label}
              </span>
              {tab.count > 0 && (
                <span
                  style={{
                    background: activeTab === tab.id ? '#2d8a87' : 'rgba(255,255,255,0.08)',
                    color: activeTab === tab.id ? '#060d12' : 'rgba(212,197,169,0.4)',
                    fontFamily: 'monospace',
                    fontSize: 8,
                    padding: '1px 6px',
                    borderRadius: 10,
                    fontWeight: 700,
                  }}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'observations' && (
          <div>
            {/* Emoji reaction legend */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 20,
                padding: '10px 16px',
                background: 'rgba(201,168,76,0.04)',
                border: '1px solid rgba(201,168,76,0.1)',
                borderRadius: 6,
              }}
            >
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginRight: 4 }}>
                Reactions:
              </span>
              {EMOJI_REACTIONS.map(r => (
                <span key={r.label} style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(212,197,169,0.4)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {r.emoji} <span style={{ color: 'rgba(212,197,169,0.25)' }}>{r.label}</span>
                </span>
              ))}
            </div>

            {OBSERVATION_THREADS.map(thread => (
              <ObservationThreadPanel key={thread.id} thread={thread} />
            ))}

            {/* Start new thread CTA */}
            <div
              style={{
                marginTop: 20,
                padding: '20px 24px',
                background: 'rgba(45,122,71,0.05)',
                border: '1px dashed rgba(45,122,71,0.2)',
                borderRadius: 8,
                textAlign: 'center',
              }}
            >
              <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,122,71,0.5)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                ✦ Start a New Observation Thread
              </p>
              <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, marginBottom: 14 }}>
                Observed something in creation that connects to a Name or Ayat? Open a thread and invite others to co-contribute.
              </p>
              <Link
                href="/inner-reality-lens"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontFamily: 'monospace',
                  fontSize: 9,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: '#2d7a47',
                  padding: '8px 20px',
                  background: 'rgba(45,122,71,0.1)',
                  border: '1px solid rgba(45,122,71,0.25)',
                  borderRadius: 4,
                  textDecoration: 'none',
                }}
              >
                Use the IRL Lens to Begin →
              </Link>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <DirectMessagesPanel />
        )}
      </div>
    </section>
  );
}
